/**
 * Production High-DPI (300 DPI) Rendering Utility
 * 
 * Provides dedicated, independent rendering at physical scale (inches x 300 DPI),
 * separated from the fast on-screen preview rendering (~100 DPI).
 */

export interface PhysicalDimensionsInches {
  widthInches: number;
  heightInches: number;
}

export interface DPIResolution {
  widthPx: number;
  heightPx: number;
}

/**
 * Extracts physical dimensions in inches from granite config, ESQ modal, or elements.
 */
export function getPhysicalDimensionsInches(
  graniteConfig?: any,
  designMode?: string,
  elements?: any[]
): PhysicalDimensionsInches {
  let physW = 0;
  let physH = 0;

  // 1. Check granite details size array e.g. [24, 12] or [36, 24]
  const size = graniteConfig?.granite_details?.size as number[] | undefined;
  if (Array.isArray(size) && size.length >= 2 && Number(size[0]) > 0 && Number(size[1]) > 0) {
    physW = Number(size[0]);
    physH = Number(size[1]);
  }

  // 2. Check template element or ESQ modal properties if size not found from graniteConfig
  if ((physW <= 0 || physH <= 0) && Array.isArray(elements)) {
    const tpl = elements.find((el: any) => {
      const role = String(el?.properties?.role || el?.properties?.kind || '').toLowerCase();
      return role === 'template';
    });

    if (tpl?.properties) {
      const wIn = Number(tpl.properties.esqModalWidthIn);
      const hIn = Number(tpl.properties.esqModalHeightIn);
      if (Number.isFinite(wIn) && wIn > 0 && Number.isFinite(hIn) && hIn > 0) {
        physW = wIn;
        physH = hIn;
      } else {
        const modalName = String(tpl.properties.esqModalName || '');
        const match = modalName.match(/(\d+)\s*[xX]\s*(\d+)/);
        if (match) {
          physW = Number(match[1]) || 0;
          physH = Number(match[2]) || 0;
        }
      }
    }
  }

  // 3. Fallback defaults (24 in x 12 in) if unspecified
  if (physW <= 0) physW = 24;
  if (physH <= 0) physH = 12;

  // 4. Normalize metric / implausible inputs (>240) and clamp to safe physical bounds (1-240 inches)
  if (physW > 240) physW = Math.round(physW / 2.54);
  if (physH > 240) physH = Math.round(physH / 2.54);
  physW = Math.min(240, Math.max(1, physW));
  physH = Math.min(240, Math.max(1, physH));

  return { widthInches: physW, heightInches: physH };
}

/**
 * Calculates output pixel dimensions for target DPI (default 300 DPI).
 */
export function calculate300DPIResolution(
  widthInches: number,
  heightInches: number,
  dpi: number = 300
): DPIResolution {
  return {
    widthPx: Math.min(8192, Math.round(widthInches * dpi)),
    heightPx: Math.min(8192, Math.round(heightInches * dpi))
  };
}

export interface RenderProductionOptions {
  format?: 'png' | 'jpeg';
  quality?: number;
  targetDPI?: number;
  includeBackground?: boolean;
  selectedBackground?: any;
}

/**
 * Renders a live Konva Stage at physical inches x 300 DPI resolution off-screen.
 */
export async function renderProduction300DPI(
  stage: any,
  graniteConfig?: any,
  designMode?: string,
  elements?: any[],
  options: RenderProductionOptions = {}
): Promise<string> {
  if (!stage) return '';

  const stageObj = typeof stage.getStage === 'function' ? stage.getStage() : stage;
  if (!stageObj) return '';

  const currentScaleX = (typeof stageObj.scaleX === 'function' ? stageObj.scaleX() : 1) || 1;
  const currentScaleY = (typeof stageObj.scaleY === 'function' ? stageObj.scaleY() : 1) || 1;
  const rawW = typeof stageObj.width === 'function' ? stageObj.width() : 1200;
  const rawH = typeof stageObj.height === 'function' ? stageObj.height() : 600;

  const unscaledW = Math.max(1, Math.round(rawW / currentScaleX));
  const unscaledH = Math.max(1, Math.round(rawH / currentScaleY));
  const stageAspect = unscaledW / unscaledH;

  const targetDPI = options.targetDPI ?? 300;
  let targetW: number;
  let targetH: number;

  if (targetDPI === 150) {
    // Fast preview path: cap max dimension at 1800px
    const maxDim = 1800;
    if (stageAspect >= 1) {
      targetW = maxDim;
      targetH = Math.round(maxDim / stageAspect);
    } else {
      targetH = maxDim;
      targetW = Math.round(maxDim * stageAspect);
    }
  } else {
    // 300 DPI High-Quality Print Path: calculate resolution based on physical stone dimensions while preserving stage aspect ratio
    const physical = getPhysicalDimensionsInches(graniteConfig, designMode, elements);
    const maxPhysicalInches = Math.max(physical.widthInches, physical.heightInches);
    // Clamp max dimension to 8192px to prevent Safari/iOS WebKit canvas memory overflow (16.7 MP limit)
    const baseDim = Math.min(8192, Math.round(maxPhysicalInches * targetDPI));

    if (stageAspect >= 1) {
      targetW = baseDim;
      targetH = Math.round(baseDim / stageAspect);
    } else {
      targetH = baseDim;
      targetW = Math.round(baseDim * stageAspect);
    }
  }

  const pixelRatio = targetW / unscaledW;

  let dataURL = '';
  const tmpContainer = document.createElement('div');
  tmpContainer.style.position = 'fixed';
  tmpContainer.style.left = '-10000px';
  tmpContainer.style.top = '-10000px';
  document.body.appendChild(tmpContainer);

  try {
    const cloned = stageObj.clone({ container: tmpContainer });
    try {
      // Reset cloned stage transform to 1:1 scale, (0,0) position & offset, and unscaled dimensions
      if (typeof cloned.width === 'function') cloned.width(unscaledW);
      if (typeof cloned.height === 'function') cloned.height(unscaledH);
      if (typeof cloned.scale === 'function') cloned.scale({ x: 1, y: 1 });
      if (typeof cloned.position === 'function') cloned.position({ x: 0, y: 0 });
      if (typeof cloned.offset === 'function') cloned.offset({ x: 0, y: 0 });
      if (typeof cloned.rotation === 'function') cloned.rotation(0);
      // Hide selection & UI elements
      cloned.find('Transformer').forEach((transformer: any) => { transformer.visible(false); });
      cloned.find('Group').forEach((group: any) => {
        if (group.attrs.name === 'selection-group' || group.attrs.name === 'ui-overlay') {
          group.visible(false);
        }
      });
      cloned.find('Rect').forEach((rect: any) => {
        if (rect.attrs.fill === 'rgba(59, 130, 246, 0.2)' ||
            rect.attrs.stroke === 'rgba(59, 130, 246, 0.8)' ||
            rect.attrs.fill === 'rgba(93, 114, 44, 0.2)' ||
            rect.attrs.stroke === 'rgba(93, 114, 44, 0.8)') {
          rect.visible(false);
        }
      });
      cloned.find('Shape').forEach((shape: any) => {
        try {
          const stroke = String(shape?.attrs?.stroke || '').toLowerCase();
          const dash = shape?.attrs?.dash;
          const strokeWidth = Number(shape?.attrs?.strokeWidth || 0);
          const hasDash = (Array.isArray(dash) && dash.length >= 2) || (typeof dash === 'string' && dash.length > 0);
          const isAnyDashedOutline = hasDash && strokeWidth >= 1;
          if ((strokeWidth >= 2 && (
            stroke === '#007bff' || stroke === '#3b82f6' ||
            stroke.includes('59, 130, 246') || stroke.includes('59,130,246') ||
            stroke.includes('0, 123, 255') || stroke.includes('0,123,255')
          )) || isAnyDashedOutline) {
            shape.visible(false);
          }
        } catch { }
      });
      try {
        const gl = cloned.findOne && (cloned.findOne('#guides-layer') || cloned.findOne('.guides-layer'));
        if (gl) gl.visible(false);
      } catch { }

      // Ensure images are fully loaded
      try {
        const konvaImages = cloned.find('Image');
        const imgPromises: Promise<void>[] = [];
        konvaImages.forEach((kImg: any) => {
          const imgObj = kImg.image();
          if (imgObj && !imgObj.complete) {
            imgPromises.push(new Promise<void>((resolve) => {
              imgObj.onload = () => resolve();
              imgObj.onerror = () => resolve();
              setTimeout(resolve, 3000);
            }));
          }
        });
        if (imgPromises.length > 0) {
          await Promise.all(imgPromises);
        }
      } catch { }

      const mimeType = options.format === 'jpeg' ? 'image/jpeg' : 'image/png';
      const quality = options.quality ?? 1;

      let effectivePixelRatio = pixelRatio;
      try {
        const physical = getPhysicalDimensionsInches(graniteConfig, designMode, elements);
        const stoneRes = calculate300DPIResolution(physical.widthInches, physical.heightInches, targetDPI);
        const graniteNodes = cloned.find((node: any) => {
          const name = String(node?.attrs?.name || node?.name || '').toLowerCase();
          const role = String(node?.attrs?.role || node?.role || '').toLowerCase();
          const id = String(node?.attrs?.id || node?.id || '').toLowerCase();
          return name.includes('granite') || role.includes('template') || role.includes('granite') || id.includes('granite') || name.includes('template');
        });
        if (graniteNodes && graniteNodes.length > 0) {
          const stoneNode = graniteNodes[0];
          const box = (typeof stoneNode.getClientRect === 'function') ? stoneNode.getClientRect({ relativeTo: cloned }) : null;
          if (box && box.width > 0) {
            const stoneRatio = stoneRes.widthPx / box.width;
            effectivePixelRatio = Math.min(stoneRatio, 8192 / unscaledW);
          }
        }
      } catch { }

      const rawStageDataURL = cloned.toDataURL({
        mimeType,
        quality,
        pixelRatio: effectivePixelRatio
      });

      // Composite background only if explicitly requested with a valid selectedBackground
      const shouldIncludeBg = options.includeBackground === true && Boolean(options.selectedBackground);
      if (shouldIncludeBg) {
        const bgObj = options.selectedBackground;
        let bgUrl = typeof bgObj === 'string' ? bgObj : (bgObj && (bgObj.image_url || bgObj.src));
        if (!bgUrl) {
          bgUrl = '/grass_bg.jpg';
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext('2d');

        if (ctx) {
          try {
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
          } catch { }

          if (bgUrl) {
            const imgBg = new Image();
            imgBg.crossOrigin = 'anonymous';
            await new Promise<void>((res) => { imgBg.onload = () => res(); imgBg.onerror = () => res(); imgBg.src = bgUrl; });
            if (imgBg.width > 0 && imgBg.height > 0) {
              const rBg = imgBg.width / imgBg.height;
              const rCv = targetW / targetH;
              let dw = targetW, dh = targetH, dx = 0, dy = 0;
              if (rBg > rCv) {
                dh = targetH; dw = Math.ceil(targetH * rBg); dx = Math.floor((targetW - dw) / 2);
              } else {
                dw = targetW; dh = Math.ceil(targetW / rBg); dy = Math.floor((targetH - dh) / 2);
              }
              ctx.drawImage(imgBg, dx, dy, dw, dh);
            } else {
              ctx.fillStyle = (bgObj as any)?.background_color || '#4a6b32';
              ctx.fillRect(0, 0, targetW, targetH);
            }
          } else {
            ctx.fillStyle = (bgObj as any)?.background_color || '#e0e0e0';
            ctx.fillRect(0, 0, targetW, targetH);
          }

          const stageImg = new Image();
          await new Promise<void>((res) => { stageImg.onload = () => res(); stageImg.onerror = () => res(); stageImg.src = rawStageDataURL; });
          ctx.drawImage(stageImg, 0, 0, targetW, targetH);

          dataURL = canvas.toDataURL(mimeType, quality);
        } else {
          dataURL = rawStageDataURL;
        }
      } else {
        dataURL = rawStageDataURL;
      }
    } finally {
      cloned.destroy();
    }
  } finally {
    try { document.body.removeChild(tmpContainer); } catch { }
  }

  if (!dataURL || dataURL.length < 100 || dataURL === 'data:,' || dataURL.startsWith('data:,')) {
    throw new Error('Canvas high-DPI render failed: returned invalid or blank data URL.');
  }

  return dataURL;
}

/**
 * Generates a production render directly from raw design JSON data preserving original aspect ratio.
 */
export async function renderProductionFromDesignJson300DPI(
  designData: any,
  graniteConfig?: any,
  designMode?: string,
  options: RenderProductionOptions = {}
): Promise<string> {
  const Konva = (await import('konva')).default;
  const elements = Array.isArray(designData?.elements) ? designData.elements : [];

  let baseW = Number(designData?.canvas_width || 0);
  let baseH = Number(designData?.canvas_height || 0);

  if (baseW <= 0 || baseH <= 0) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    elements.forEach((el: any) => {
      const x = el.x || 0;
      const y = el.y || 0;
      const w = el.width || 0;
      const h = el.height || 0;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + w);
      maxY = Math.max(maxY, y + h);
    });
    if (minX !== Infinity && maxX > minX && maxY > minY) {
      baseW = maxX - minX + 80;
      baseH = maxY - minY + 80;
    } else {
      baseW = 1200;
      baseH = 600;
    }
  }

  const designAspect = baseW / baseH;
  let targetW: number;
  let targetH: number;

  if (options.targetDPI === 150) {
    const maxDim = 1800;
    if (designAspect >= 1) {
      targetW = maxDim;
      targetH = Math.round(maxDim / designAspect);
    } else {
      targetH = maxDim;
      targetW = Math.round(maxDim * designAspect);
    }
  } else {
    const physical = getPhysicalDimensionsInches(graniteConfig, designMode, elements);
    const maxPhysicalInches = Math.max(physical.widthInches, physical.heightInches);
    // Clamp max dimension to 8192px to prevent Safari/iOS WebKit canvas memory overflow (16.7 MP limit)
    const baseDim = Math.min(8192, Math.round(maxPhysicalInches * (options.targetDPI || 300)));
    if (designAspect >= 1) {
      targetW = baseDim;
      targetH = Math.round(baseDim / designAspect);
    } else {
      targetH = baseDim;
      targetW = Math.round(baseDim * designAspect);
    }
  }

  const scale = Math.min(targetW / baseW, targetH / baseH);

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.style.top = '-10000px';
  document.body.appendChild(container);

  try {
    const stage = new Konva.Stage({ container, width: targetW, height: targetH });
    const layer = new Konva.Layer();
    stage.add(layer);

    // Background (fallback to /grass_bg.jpg if none specified)
    const bgInfo = options.selectedBackground || designData?.background;
    let bgSrc = typeof bgInfo === 'string' ? bgInfo : (bgInfo && (bgInfo.image_url || bgInfo.src));
    if (!bgSrc) {
      bgSrc = '/grass_bg.jpg';
    }

    if (bgSrc) {
      await new Promise<void>((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const k = new Konva.Image({ image: img, x: 0, y: 0, width: targetW, height: targetH });
          layer.add(k);
          resolve();
        };
        img.onerror = () => {
          const rect = new Konva.Rect({ x: 0, y: 0, width: targetW, height: targetH, fill: '#ffffff' });
          layer.add(rect);
          resolve();
        };
        img.src = bgSrc;
      });
    } else {
      const rect = new Konva.Rect({ x: 0, y: 0, width: targetW, height: targetH, fill: '#ffffff' });
      layer.add(rect);
    }

    // Elements with uniform scaling (zero stretching)
    for (const el of elements) {
      const x = (el.x || 0) * scale;
      const y = (el.y || 0) * scale;
      const w = (el.width || 0) * scale;
      const h = (el.height || 0) * scale;

      if (el.type === 'tablet' || el.type === 'base') {
        const rect = new Konva.Rect({
          x, y, width: w, height: h,
          fill: el.properties?.texture || '#8b8b8b',
          stroke: '#666',
          strokeWidth: 2 * scale,
          cornerRadius: 8 * scale
        });
        layer.add(rect);
      } else if (el.type === 'artwork' && el?.properties?.src) {
        try {
          await new Promise<void>((res) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              const kimg = new Konva.Image({
                image: img, x, y, width: w, height: h,
                rotation: el.rotation || 0
              });
              layer.add(kimg);
              res();
            };
            img.onerror = () => res();
            img.src = el.properties.src;
          });
        } catch { }
      } else if (el.type === 'text') {
        const textContent = String(el.properties?.content || el.properties?.text || '').trim();
        const computedFs = (el.properties?.fontSize || 16) * scale;
        if (textContent !== '' && computedFs > 0) {
          const txt = new Konva.Text({
            x, y,
            width: w > 0 ? w : undefined,
            height: h > 0 ? h : undefined,
            text: el.properties?.content || el.properties?.text || '',
            fontSize: Math.max(1, computedFs),
            fontFamily: el.properties?.fontFamily || 'Times New Roman',
            fill: el.properties?.color || el.properties?.fill || '#000',
            align: el.properties?.align || 'left',
            verticalAlign: el.properties?.verticalAlign || 'top',
            rotation: el.rotation || 0
          });
          layer.add(txt);
        }
      }
    }

    stage.batchDraw();
    await new Promise(r => setTimeout(r, 200));

    const mime = options.format === 'jpeg' ? 'image/jpeg' : 'image/png';
    const dataURL = stage.toDataURL({ mimeType: mime, quality: options.quality ?? 1, pixelRatio: 1 });
    stage.destroy();

    if (!dataURL || dataURL.length < 100 || dataURL === 'data:,' || dataURL.startsWith('data:,')) {
      throw new Error('Canvas high-DPI JSON render failed: returned invalid or blank data URL.');
    }

    return dataURL;
  } finally {
    try { document.body.removeChild(container); } catch { }
  }
}

/**
 * Estimates byte size of a base64 data URL.
 */
export function estimateDataUrlBytes(dataUrl: string): number {
  try {
    const idx = dataUrl.indexOf(',');
    if (idx < 0) return dataUrl.length;
    const b64 = dataUrl.slice(idx + 1);
    const padding = b64.endsWith('==') ? 2 : (b64.endsWith('=') ? 1 : 0);
    return Math.floor((b64.length * 3) / 4) - padding;
  } catch {
    return dataUrl.length;
  }
}

/**
 * Compresses/downscales any image dataURL while strictly preserving original aspect ratio (target <= 4.8 MB).
 */
export async function compressDataUrlToLimit(
  dataUrl: string,
  maxBytes: number = 4800000
): Promise<string> {
  if (!dataUrl) return '';
  if (estimateDataUrlBytes(dataUrl) <= maxBytes) return dataUrl;

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const isJpg = dataUrl.startsWith('data:image/jpeg') || dataUrl.startsWith('data:image/jpg');
      const origAspect = (img.width || 1) / (img.height || 1);
      let currentWidth = img.width;
      let currentHeight = img.height;
      let quality = isJpg ? 0.88 : 0.92;

      const canvas = document.createElement('canvas');
      canvas.width = currentWidth;
      canvas.height = currentHeight;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, currentWidth, currentHeight);

      let resultUrl = canvas.toDataURL(isJpg ? 'image/jpeg' : 'image/png', quality);

      let attempts = 0;
      while (estimateDataUrlBytes(resultUrl) > maxBytes && attempts < 8) {
        attempts++;
        if (isJpg && quality > 0.45) {
          quality -= 0.10;
        } else {
          currentWidth = Math.max(800, Math.floor(currentWidth * 0.85));
          currentHeight = Math.max(600, Math.round(currentWidth / origAspect));
          canvas.width = currentWidth;
          canvas.height = currentHeight;
          ctx.drawImage(img, 0, 0, currentWidth, currentHeight);
        }
        resultUrl = canvas.toDataURL(isJpg ? 'image/jpeg' : 'image/png', quality);
      }

      if (!isJpg && estimateDataUrlBytes(resultUrl) > maxBytes) {
        resultUrl = canvas.toDataURL('image/jpeg', 0.82);
      }

      resolve(resultUrl);
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

/**
 * Creates a high quality PDF maintaining exact aspect ratio, guaranteed <= 4.8 MB.
 */
export async function createCompressedPdf(
  frontDataUrl: string,
  backDataUrl?: string | null,
  fileName: string = 'design.pdf',
  maxBytes: number = 4800000
): Promise<void> {
  const { jsPDF } = await import('jspdf');

  const pageCount = backDataUrl ? 2 : 1;
  const initialTarget = Math.floor((maxBytes * 0.75) / pageCount);

  let finalPdf: any = null;
  let finalBlobSize = Infinity;
  let attempts = 0;

  while (attempts < 4 && finalBlobSize > maxBytes) {
    attempts++;
    const currentTarget = Math.floor(initialTarget * Math.pow(0.70, attempts - 1));

    const compressedFront = await compressDataUrlToLimit(frontDataUrl, currentTarget);
    const compressedBack = backDataUrl ? await compressDataUrlToLimit(backDataUrl, currentTarget) : null;

    const imgTmp = new Image();
    await new Promise<void>((res) => {
      imgTmp.onload = () => res();
      imgTmp.onerror = () => res();
      imgTmp.src = compressedFront;
    });
    const w = Math.max(1, imgTmp.width || 1920);
    const h = Math.max(1, imgTmp.height || 960);
    const aspect = w / h;

    let pdfW = 2200;
    let pdfH = Math.round(2200 / aspect);
    if (w < h) {
      pdfH = 2200;
      pdfW = Math.round(2200 * aspect);
    }

    const pdf = new jsPDF({
      orientation: pdfW >= pdfH ? 'landscape' : 'portrait',
      unit: 'px',
      format: [pdfW, pdfH]
    });
    pdf.addImage(compressedFront, 'JPEG', 0, 0, pdfW, pdfH, undefined, 'FAST');

    if (compressedBack) {
      pdf.addPage([pdfW, pdfH], pdfW >= pdfH ? 'landscape' : 'portrait');
      pdf.addImage(compressedBack, 'JPEG', 0, 0, pdfW, pdfH, undefined, 'FAST');
    }

    const blob = pdf.output('blob');
    finalBlobSize = blob.size;
    finalPdf = pdf;

    if (finalBlobSize <= maxBytes) {
      break;
    }
  }

  if (finalPdf) {
    finalPdf.save(fileName);
  }
}

